import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { getAccountType } from "../utility/accountType";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../services/NotificationService";

const NotificationContext = createContext(null);

// A single shared instance (same pattern as ShortlistContext) so the header bell (desktop +
// mobile) and the student dashboard's Notifications section all read/write the exact same
// state — marking something read in one place updates every place it's shown, and there's
// only one 60s poll loop for the whole app, not one per consumer.
export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const isStudent = Boolean(Cookies.get("token")) && getAccountType() === "student";

  const refreshUnreadCount = useCallback(() => {
    if (!isStudent) return;
    getUnreadCount()
      .then((res) => setUnreadCount(res.data?.count || 0))
      .catch(() => {});
  }, [isStudent]);

  const loadList = useCallback(() => {
    if (!isStudent) return;
    setLoading(true);
    getNotifications(0, 15)
      .then((res) => setNotifications(res.data?.content || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isStudent]);

  const markRead = useCallback(
    (id) => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      markNotificationRead(id)
        .catch(() => {})
        .then(refreshUnreadCount);
    },
    [refreshUnreadCount]
  );

  const markAllRead = useCallback(() => {
    markAllNotificationsRead()
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isStudent) {
      setUnreadCount(0);
      setNotifications([]);
      return undefined;
    }
    refreshUnreadCount();
    loadList();
    const id = setInterval(refreshUnreadCount, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent]);

  const value = useMemo(
    () => ({ unreadCount, notifications, loading, loadList, markRead, markAllRead }),
    [unreadCount, notifications, loading, loadList, markRead, markAllRead]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotificationContext = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotificationContext must be used within a NotificationProvider");
  return ctx;
};
